import { useCallback, useEffect, useRef, useState } from "react";
import type { StandardSetSummary } from "../../auth/api-client";
import { MentionPicker } from "./MentionPicker";
import {
  emptyMentionDoc,
  type MentionPromptDoc,
  type MentionSegment,
  type MentionToken,
} from "./mentionTypes";
import { type CatalogPickerItem, useStandardsCatalog } from "./useStandardsCatalog";

interface Props {
  readonly bearer: string | null;
  readonly value: MentionPromptDoc;
  readonly onChange: (doc: MentionPromptDoc) => void;
  readonly standardSetId: string;
  readonly onStandardSetIdChange?: (id: string) => void;
  readonly standardSets?: readonly StandardSetSummary[];
  readonly showSetSelector?: boolean;
  readonly disabled?: boolean;
  readonly minRows?: number;
  readonly placeholder?: string;
  readonly className?: string;
}

function mergeAdjacentText(segments: MentionSegment[]): MentionSegment[] {
  const out: MentionSegment[] = [];
  for (const s of segments) {
    if (s.type === "text" && !s.value) continue;
    const prev = out[out.length - 1];
    if (s.type === "text" && prev?.type === "text") {
      out[out.length - 1] = { type: "text", value: prev.value + s.value };
    } else {
      out.push(s);
    }
  }
  if (out.length === 0) out.push({ type: "text", value: "" });
  return out;
}

function parseEditorRoot(root: HTMLElement): MentionSegment[] {
  const segments: MentionSegment[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node.textContent ?? "";
      if (t) segments.push({ type: "text", value: t });
      return;
    }
    if (node instanceof HTMLElement && node.dataset.mentionId) {
      segments.push({
        type: "mention",
        token: {
          kind: (node.dataset.mentionKind as MentionToken["kind"]) ?? "standard",
          id: node.dataset.mentionId ?? "",
          label: node.dataset.mentionLabel ?? "",
          description: node.dataset.mentionDescription ?? "",
        },
      });
      return;
    }
    node.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  return mergeAdjacentText(segments);
}

function buildEditorHtml(segments: readonly MentionSegment[]): string {
  return segments
    .map((s) => {
      if (s.type === "text") {
        return s.value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>");
      }
      const t = s.token;
      return `<span class="mention-pill" contenteditable="false" data-mention-kind="${t.kind}" data-mention-id="${t.id}" data-mention-label="${t.label.replace(/"/g, "&quot;")}" data-mention-description="${t.description.replace(/"/g, "&quot;")}">${t.label}<button type="button" class="mention-pill-remove" data-remove-mention="${t.id}" aria-label="Remove ${t.label}">×</button></span>`;
    })
    .join("");
}

export function MentionInput({
  bearer,
  value,
  onChange,
  standardSetId,
  onStandardSetIdChange,
  standardSets: setsProp,
  showSetSelector = true,
  disabled = false,
  minRows = 3,
  placeholder,
  className,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const skipSyncRef = useRef(false);

  const catalog = useStandardsCatalog(bearer, standardSetId);
  const sets = setsProp ?? catalog.sets;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerIndex, setPickerIndex] = useState(0);
  const [anchorStyle, setAnchorStyle] = useState<{ top: number; left: number } | null>(null);

  const pickerItems = catalog.filterItems(pickerQuery);

  useEffect(() => {
    if (pickerIndex >= pickerItems.length) setPickerIndex(0);
  }, [pickerIndex, pickerItems.length]);

  const syncFromEditor = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const segments = parseEditorRoot(el);
    onChange({ segments });
  }, [onChange]);

  const renderDoc = useCallback((doc: MentionPromptDoc) => {
    const el = editorRef.current;
    if (!el) return;
    skipSyncRef.current = true;
    el.innerHTML = buildEditorHtml(doc.segments) || "<br>";
    skipSyncRef.current = false;
  }, []);

  useEffect(() => {
    if (skipSyncRef.current) return;
    const el = editorRef.current;
    if (!el) return;
    const current = parseEditorRoot(el);
    const same =
      JSON.stringify(current) === JSON.stringify(mergeAdjacentText([...value.segments]));
    if (!same) renderDoc(value);
  }, [value, renderDoc]);

  const updatePickerAnchor = useCallback(() => {
    const sel = window.getSelection();
    const wrap = wrapRef.current;
    if (!sel || sel.rangeCount === 0 || !wrap) return;
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    setAnchorStyle({
      top: rect.bottom - wrapRect.top + 4,
      left: Math.max(0, rect.left - wrapRect.left),
    });
  }, []);

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setPickerQuery("");
    setPickerIndex(0);
    savedRangeRef.current = null;
  }, []);

  const insertMention = useCallback(
    (item: CatalogPickerItem) => {
      const el = editorRef.current;
      const range = savedRangeRef.current;
      if (!el) return;

      const token: MentionToken = {
        kind: item.kind,
        id: item.id,
        label: item.label,
        description: item.description,
      };

      el.focus();
      const sel = window.getSelection();
      if (sel && range) {
        sel.removeAllRanges();
        sel.addRange(range);
      }

      const pill = document.createElement("span");
      pill.className = "mention-pill";
      pill.contentEditable = "false";
      pill.dataset.mentionKind = token.kind;
      pill.dataset.mentionId = token.id;
      pill.dataset.mentionLabel = token.label;
      pill.dataset.mentionDescription = token.description;
      pill.textContent = token.label;
      const rm = document.createElement("button");
      rm.type = "button";
      rm.className = "mention-pill-remove";
      rm.dataset.removeMention = token.id;
      rm.setAttribute("aria-label", `Remove ${token.label}`);
      rm.textContent = "×";
      pill.appendChild(rm);

      const space = document.createTextNode(" ");
      const r = sel?.rangeCount ? sel.getRangeAt(0) : null;
      if (r) {
        r.deleteContents();
        r.insertNode(space);
        r.insertNode(pill);
        r.setStartAfter(space);
        r.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(r);
      } else {
        el.appendChild(pill);
        el.appendChild(space);
      }

      closePicker();
      syncFromEditor();
    },
    [closePicker, syncFromEditor]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (pickerOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setPickerIndex((i) => (pickerItems.length ? (i + 1) % pickerItems.length : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setPickerIndex((i) =>
          pickerItems.length ? (i - 1 + pickerItems.length) % pickerItems.length : 0
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const item = pickerItems[pickerIndex];
        if (item) insertMention(item);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closePicker();
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        setPickerQuery((q) => q.slice(0, -1));
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setPickerQuery((q) => q + e.key);
        return;
      }
    }

    if (e.key === "@" && !pickerOpen) {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      }
      setPickerOpen(true);
      setPickerQuery("");
      setPickerIndex(0);
      updatePickerAnchor();
    }
  };

  const handleInput = () => {
    if (skipSyncRef.current) return;
    syncFromEditor();
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.dataset.removeMention) {
      e.preventDefault();
      const pill = target.closest(".mention-pill");
      pill?.remove();
      syncFromEditor();
    }
  };

  const showSelector = showSetSelector && sets.length > 1;

  return (
    <div className={`mention-input-wrap${className ? ` ${className}` : ""}`} ref={wrapRef}>
      {showSelector ? (
        <label className="mention-input-set field" style={{ marginBottom: "0.5rem" }}>
          <span className="field-label muted small">Standard set (optional)</span>
          <select
            className="input"
            value={standardSetId}
            disabled={disabled || catalog.loading}
            onChange={(e) => onStandardSetIdChange?.(e.target.value)}
          >
            {sets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div
        ref={editorRef}
        className={`mention-input input${disabled ? " is-disabled" : ""}`}
        contentEditable={!disabled}
        role="combobox"
        aria-expanded={pickerOpen}
        aria-multiline="true"
        data-placeholder={placeholder}
        style={{ minHeight: `${minRows * 1.5}rem` }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        suppressContentEditableWarning
      />
      {pickerOpen ? (
        <div className="mention-picker-query muted small" aria-hidden>
          @{pickerQuery}
        </div>
      ) : null}
      <MentionPicker
        open={pickerOpen && pickerItems.length > 0}
        items={pickerItems}
        activeIndex={pickerIndex}
        anchorStyle={anchorStyle}
        onHover={setPickerIndex}
        onSelect={insertMention}
      />
      {catalog.error ? <p className="form-error small">{catalog.error}</p> : null}
    </div>
  );
}

export { emptyMentionDoc };
